import React, { useState, useEffect } from "react";
import { Button, Input, Row, Col, Select, DatePicker, Space } from "antd";
import TableContainer from "../../components/TableContainer";
import Heading from "../../components/Heading";
import urls from "../../api/urls";
import API from "../../api";
import {
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
} from "@ant-design/icons";

const { Option } = Select;

const DADataPage = () => {
  // Search and filter state
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({
    deliveryAgentId: "",
    fullName: "",
    isActive: undefined,
    onboardStatus: "",
    hotspotName: "",
    city: "",
    state: "",
    contactNo: "",
    createdDate: undefined,
  });
  const [tableData, setTableData] = useState([]); // Will connect with API later
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const formatDateParam = (val) => {
    if (!val) return undefined;
    if (typeof val === "string") return val;
    if (typeof val.format === "function") return val.format("YYYY-MM-DD");
    if (val instanceof Date) return val.toISOString().slice(0, 10);
    return undefined;
  };

  // Column filter props for AntD Table
  const getColumnSearchProps = (dataIndex, placeholder, customType) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        {customType === "boolean" ? (
          <Select
            style={{ marginBottom: 8, display: "block" }}
            placeholder={`Filter ${placeholder}`}
            value={selectedKeys[0]}
            onChange={(val) => {
              setSelectedKeys(val !== undefined ? [val] : []);
            }}
            allowClear
          >
            <Option value={true}>Active</Option>
            <Option value={false}>Inactive</Option>
          </Select>
        ) : customType === "date" ? (
          <DatePicker
            style={{ marginBottom: 8, display: "block" }}
            value={selectedKeys[0]}
            onChange={(date) => {
              setSelectedKeys(date ? [date] : []);
            }}
            allowClear
            format="YYYY-MM-DD"
          />
        ) : (
          <Input
            placeholder={`Search ${placeholder}...`}
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => {
              confirm();
            }}
            style={{
              marginBottom: 8,
              display: "block",
              borderRadius: 6,
            }}
            allowClear={false}
          />
        )}
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<SearchOutlined />}
            onClick={() => confirm()}
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            size="small"
            style={{ width: 90 }}
            onClick={() => {
              clearFilters();
              confirm();
            }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <FilterOutlined
        style={{
          color: filtered ? "#1890ff" : undefined,
        }}
      />
    ),
    onFilterDropdownVisibleChange: () => {
      // Focus input automatically
    },
    filteredValue:
      filters[dataIndex] !== undefined && filters[dataIndex] !== ""
        ? [filters[dataIndex]]
        : null,
  });

  // Table columns with filters
  const columns = [
    {
      title: "Inno Fulfill ID",
      dataIndex: "deliveryAgentId",
      key: "deliveryAgentId",
      ...getColumnSearchProps("deliveryAgentId", "Inno Fulfill ID"),
    },
    {
      title: "Staff Full Name",
      dataIndex: "fullName",
      key: "fullName",
      ...getColumnSearchProps("fullName", "Staff Full Name"),
    },
    {
      title: "ID Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (val) => (val ? "Active" : "Inactive"),
      ...getColumnSearchProps("isActive", "ID Status", "boolean"),
    },
    {
      title: "Onboarding Status",
      dataIndex: "onboardStatus",
      key: "onboardStatus",
      ...getColumnSearchProps("onboardStatus", "Onboarding Status"),
    },
    {
      title: "Hotspot Name",
      dataIndex: "hotspotName",
      key: "hotspotName",
      ...getColumnSearchProps("hotspotName", "Hotspot Name"),
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      ...getColumnSearchProps("city", "City"),
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      ...getColumnSearchProps("state", "State"),
    },
    {
      title: "Contact No",
      dataIndex: "contactNo",
      key: "contactNo",
      ...getColumnSearchProps("contactNo", "Contact No"),
    },
    {
      title: "ID Creation Date",
      dataIndex: "createdDate",
      key: "createdDate",
      render: (val) => (val ? new Date(val).toLocaleDateString() : "-"),
      ...getColumnSearchProps("createdDate", "ID Creation Date", "date"),
    },
  ];

  const buildParams = (
    isExport = false,
    customFilters = null,
    customSearch = null
  ) => {
    const activeFilters = customFilters !== null ? customFilters : filters;
    const activeSearch = customSearch !== null ? customSearch : searchText;

    const params = {
      page: pagination.current,
      limit: pagination.pageSize,
      search: activeSearch || undefined,
      deliveryAgentId: activeFilters.deliveryAgentId || undefined,
      fullName: activeFilters.fullName || undefined,
      contactNo: activeFilters.contactNo || undefined,
      isActive: activeFilters.isActive,
      city: activeFilters.city || undefined,
      state: activeFilters.state || undefined,
      hotspotName: activeFilters.hotspotName || undefined,
      onboardStatus: activeFilters.onboardStatus || undefined,
      createdDate: formatDateParam(activeFilters.createdDate),
    };
    if (isExport) {
      params.export = true;
      params.format = "csv";
    }
    // remove undefined
    Object.keys(params).forEach(
      (k) => params[k] === undefined && delete params[k]
    );
    return params;
  };

  const handleSearchWithFilters = async (
    page = 1,
    pageSize = 20,
    customFilters = null,
    customSearch = null
  ) => {
    setLoading(true);
    try {
      setPagination((prev) => ({ ...prev, current: page, pageSize }));
      const params = buildParams(false, customFilters, customSearch);
      params.page = String(page);
      params.limit = String(pageSize);

      const query = new URLSearchParams(params).toString();
      const apiUrl = `${urls.GET_DA_DETAILS}?${query}`;
      const res = await API.Api(apiUrl).get();
      if (res.status) {
        // API already returns data in the correct format, just use it directly
        setTableData(res.response.data.data || []);
        setPagination((prev) => ({
          ...prev,
          total: res.response?.data?.totalRecords || 0,
        }));
      } else {
        setTableData([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
      }
    } catch (e) {
      setTableData([]);
      setPagination((prev) => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (page = 1, pageSize = 20) => {
    return handleSearchWithFilters(page, pageSize, null, null);
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      const params = buildParams(true);
      const query = new URLSearchParams(params).toString();
      const apiUrl = `${urls.GET_DA_DETAILS}?${query}`;
      const res = await API.Api(apiUrl).getWithResponseType();

      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `da_data_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (e) {
      // handle error (could show message)
    } finally {
      setLoading(false);
    }
  };

  // Update filters and trigger search on table filter change
  const handleTableChange = (pag, tableFilters) => {
    // Update filter state to reflect AntD Table's own filtered values
    const nextFilters = {
      deliveryAgentId: "",
      fullName: "",
      isActive: undefined,
      onboardStatus: "",
      hotspotName: "",
      city: "",
      state: "",
      contactNo: "",
      createdDate: undefined,
    };

    Object.keys(tableFilters).forEach((k) => {
      if (tableFilters[k] && tableFilters[k].length > 0) {
        nextFilters[k] = tableFilters[k][0];
      }
    });

    setFilters(nextFilters);
    setPagination((prev) => ({
      ...prev,
      current: pag.current,
      pageSize: pag.pageSize,
    }));

    // Call handleSearch with the new filters directly to avoid stale state
    handleSearchWithFilters(pag.current, pag.pageSize, nextFilters);
  };

  const handleRefresh = () => {
    const emptyFilters = {
      deliveryAgentId: "",
      fullName: "",
      isActive: undefined,
      onboardStatus: "",
      hotspotName: "",
      city: "",
      state: "",
      contactNo: "",
      createdDate: undefined,
    };
    setSearchText("");
    setFilters(emptyFilters);
    setPagination((prev) => ({ ...prev, current: 1 }));
    // Call with empty filters and search directly
    handleSearchWithFilters(1, pagination.pageSize, emptyFilters, "");
  };

  const handleClearFilters = () => {
    const emptyFilters = {
      deliveryAgentId: "",
      fullName: "",
      isActive: undefined,
      onboardStatus: "",
      hotspotName: "",
      city: "",
      state: "",
      contactNo: "",
      createdDate: undefined,
    };
    setFilters(emptyFilters);
    setPagination((prev) => ({ ...prev, current: 1 }));
    // Call with empty filters directly, keep search text
    handleSearchWithFilters(1, pagination.pageSize, emptyFilters, searchText);
  };

  useEffect(() => {
    handleSearch(1, pagination.pageSize);
  }, []);

  return (
    <div className="p-1">
      <Heading title="DA Data" />
      <div style={{ padding: 24 }}>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 16 }}
        >
          {/* <Col>
            <Input.Search
              placeholder="Search by name, contact, etc."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={() => handleSearch(1, pagination.pageSize)}
              style={{ width: 300 }}
            />
          </Col> */}
          <Col>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
                Refresh
              </Button>
              <Button onClick={handleClearFilters}>Clear Filters</Button>
              <Button type="primary" onClick={handleExport}>
                Export CSV
              </Button>
            </Space>
          </Col>
        </Row>
        <TableContainer
          columns={columns}
          dataSource={tableData}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            itemRender: (page, type, originalElement) => {
              if (type === "prev") return <span>Previous</span>;
              if (type === "next") return <span>Next</span>;
              return originalElement;
            },
          }}
          onChange={handleTableChange}
          rowKey="deliveryAgentId"
        />
      </div>
    </div>
  );
};

export default DADataPage;
